import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError } from "../lib/api";
import { useToast } from "../context/ToastContext";
import type { ImageDTO, ImageListQuery, ImageListResponse } from "@shared/types";

export function useImages(query: ImageListQuery) {
  const toast = useToast();
  const [data, setData] = useState<ImageListResponse>({ images: [], total: 0, page: 1, pageSize: 60 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const key = JSON.stringify(query);
  const reqId = useRef(0);

  const load = useCallback(
    async (opts: { quiet?: boolean } = {}) => {
      const id = ++reqId.current;
      if (!opts.quiet) setLoading(true);
      setError(null);
      try {
        const res = await api.listImages(JSON.parse(key) as ImageListQuery);
        if (id === reqId.current) setData(res);
      } catch (err) {
        if (id === reqId.current) {
          const msg = err instanceof ApiError ? err.message : "Could not load images.";
          setError(msg);
          toast.error(msg);
        }
      } finally {
        if (id === reqId.current) setLoading(false);
      }
    },
    [key, toast],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const patchImage = useCallback((updated: ImageDTO) => {
    setData((d) => ({ ...d, images: d.images.map((i) => (i.id === updated.id ? updated : i)) }));
  }, []);

  const removeImage = useCallback((id: string) => {
    setData((d) => ({ ...d, images: d.images.filter((i) => i.id !== id), total: Math.max(0, d.total - 1) }));
  }, []);

  const prependImages = useCallback((imgs: ImageDTO[]) => {
    if (imgs.length === 0) return;
    setData((d) => {
      const existing = new Set(d.images.map((i) => i.id));
      const fresh = imgs.filter((i) => !existing.has(i.id));
      return { ...d, images: [...fresh, ...d.images], total: d.total + fresh.length };
    });
  }, []);

  return { ...data, loading, error, reload: load, patchImage, removeImage, prependImages };
}
